import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import { useLang, scrollToId } from "../App";
import { LOGOS } from "../images";
import { propertiesI18n } from "../i18n";
import { EASE } from "./Reveal";

const LANGS = ["fr", "de", "en"];

export default function Header() {
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "accueil", label: t.footer.home, testid: "nav-link-home" },
    { id: "expertise", label: t.nav.expertise, testid: "nav-link-expertise" },
    { id: "approche", label: t.nav.about, testid: "nav-link-about" },
    { id: "objekte", label: propertiesI18n[lang].navLabel, testid: "nav-link-properties" },
    { id: "contact", label: t.nav.contact, testid: "nav-link-contact" },
  ];

  const go = (id) => {
    setOpen(false);
    if (id === "objekte") {
      navigate("/objekte");
      return;
    }
    setTimeout(() => scrollToId(id), open ? 350 : 0);
  };

  const light = scrolled;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          light
            ? "border-b border-ink/10 bg-ivory/90 backdrop-blur-md"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 md:px-10 lg:px-14">
          <button
            data-testid="header-logo"
            onClick={() => go("accueil")}
            className="relative block"
            aria-label="FLEN Immobilier"
          >
            <img
              src={LOGOS.horizontalNegativ}
              alt="FLEN Immobilier"
              className={`block h-10 w-auto transition-opacity duration-500 md:h-12 ${light ? "opacity-0" : "opacity-100"}`}
            />
            <img
              src={LOGOS.horizontal}
              alt=""
              aria-hidden="true"
              className={`absolute inset-0 h-full w-auto transition-opacity duration-500 ${light ? "opacity-100" : "opacity-0"}`}
            />
          </button>

          <nav className="hidden items-center gap-9 lg:flex" aria-label="Main">
            {links.map((l) =>
              l.id === "objekte" ? (
                <div key={l.id} className="group relative" data-testid="nav-properties-wrap">
                  <button
                    data-testid={l.testid}
                    onClick={() => go(l.id)}
                    className={`group/btn relative text-[12px] font-medium uppercase tracking-[0.22em] transition-colors duration-300 ${
                      light ? "text-ink/70 hover:text-ink" : "text-ivory/80 hover:text-ivory"
                    }`}
                  >
                    {l.label}
                    <span className="absolute -bottom-1 left-0 h-px w-0 bg-bronze transition-[width] duration-500 group-hover/btn:w-full" />
                  </button>
                  <div
                    data-testid="nav-properties-submenu"
                    className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 pt-5 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100"
                  >
                    <div className={`flex min-w-44 flex-col border ${light ? "border-ink/10 bg-ivory" : "border-ivory/15 bg-ink"}`}>
                      <button
                        data-testid="nav-sub-properties-sale"
                        onClick={() => go("objekte")}
                        className={`px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                          light ? "text-ink/60 hover:bg-bronze/10 hover:text-ink" : "text-ivory/60 hover:bg-bronze/20 hover:text-ivory"
                        }`}
                      >
                        {propertiesI18n[lang].forSale}
                      </button>
                      <button
                        data-testid="nav-sub-properties-rental"
                        onClick={() => navigate("/rentals")}
                        className={`px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                          light ? "text-ink/60 hover:bg-bronze/10 hover:text-ink" : "text-ivory/60 hover:bg-bronze/20 hover:text-ivory"
                        }`}
                      >
                        {propertiesI18n[lang].rentalMenu}
                      </button>
                      <button
                        data-testid="nav-sub-properties-sold"
                        onClick={() => navigate("/objekte/verkauft")}
                        className={`px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                          light ? "text-ink/60 hover:bg-bronze/10 hover:text-ink" : "text-ivory/60 hover:bg-bronze/20 hover:text-ivory"
                        }`}
                      >
                        {propertiesI18n[lang].soldMenu}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  key={l.id}
                  data-testid={l.testid}
                  onClick={() => go(l.id)}
                  className={`group relative text-[12px] font-medium uppercase tracking-[0.22em] transition-colors duration-300 ${
                    light ? "text-ink/70 hover:text-ink" : "text-ivory/80 hover:text-ivory"
                  }`}
                >
                  {l.label}
                  <span className="absolute -bottom-1 left-0 h-px w-0 bg-bronze transition-[width] duration-500 group-hover:w-full" />
                </button>
              )
            )}
          </nav>

          <div className="hidden items-center gap-8 lg:flex">
            <button
              data-testid="nav-link-members"
              onClick={() => navigate("/espace")}
              className={`border px-4 py-2 text-[11px] font-medium uppercase tracking-[0.22em] transition-colors duration-500 ${
                light
                  ? "border-bronze/60 text-bronze hover:bg-bronze hover:text-ivory"
                  : "border-champagne/60 text-champagne hover:bg-champagne hover:text-ink"
              }`}
            >
              {propertiesI18n[lang] && (lang === "fr" ? "Espace membres" : lang === "de" ? "Member-Bereich" : "Member area")}
            </button>
            <div className={`flex items-center gap-2 text-[11px] tracking-[0.2em] ${light ? "text-ink/60" : "text-ivory/60"}`} data-testid="language-switcher">
              {LANGS.map((l, i) => (
                <span key={l} className="flex items-center gap-2">
                  {i > 0 && <span className={light ? "text-ink/25" : "text-ivory/25"}>|</span>}
                  <button
                    data-testid={`lang-switch-${l}`}
                    onClick={() => setLang(l)}
                    className={`uppercase transition-colors duration-300 ${
                      lang === l ? "font-semibold text-bronze" : light ? "hover:text-ink" : "hover:text-ivory"
                    }`}
                  >
                    {l}
                  </button>
                </span>
              ))}
            </div>
            <button
              data-testid="header-cta"
              onClick={() => go("contact")}
              className={`group flex items-center gap-3 border px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] transition-colors duration-500 ${
                light
                  ? "border-ink/30 text-ink hover:border-bronze hover:bg-bronze hover:text-ivory"
                  : "border-ivory/40 text-ivory hover:border-bronze hover:bg-bronze"
              }`}
            >
              {t.nav.cta}
              <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
            </button>
          </div>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(true)}
            aria-label={t.nav.openMenu}
            className={`lg:hidden ${light ? "text-ink" : "text-ivory"}`}
          >
            <Menu size={26} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-0 z-[60] flex flex-col bg-ink px-8 py-6"
          >
            <div className="flex items-center justify-between">
              <img src={LOGOS.horizontalNegativ} alt="FLEN Immobilier" className="h-7 w-auto" />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label={t.nav.closeMenu} className="text-ivory">
                <X size={28} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="mt-20 flex flex-col gap-8" aria-label="Mobile">
              {links.map((l, i) =>
                l.id === "objekte" ? (
                  <motion.div
                    key={l.id}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1 + i * 0.08, ease: EASE }}
                  >
                    <button
                      data-testid={`mobile-${l.testid}`}
                      onClick={() => setSubOpen((s) => !s)}
                      className="flex items-center gap-4 text-left font-serif text-4xl text-ivory"
                    >
                      {l.label}
                      <ChevronDown size={22} strokeWidth={1.5} className={`mt-1 text-champagne transition-transform duration-300 ${subOpen ? "rotate-180" : ""}`} />
                    </button>
                    {subOpen && (
                      <div className="mt-4 flex flex-col gap-3 border-l border-ivory/15 pl-6" data-testid="mobile-nav-properties-submenu">
                        <button
                          data-testid="mobile-nav-sub-sale"
                          onClick={() => {
                            setOpen(false);
                            navigate("/objekte");
                          }}
                          className="text-left text-[12px] font-medium uppercase tracking-[0.25em] text-ivory/70"
                        >
                          {propertiesI18n[lang].forSale}
                        </button>
                        <button
                          data-testid="mobile-nav-sub-rental"
                          onClick={() => {
                            setOpen(false);
                            navigate("/rentals");
                          }}
                          className="text-left text-[12px] font-medium uppercase tracking-[0.25em] text-ivory/70"
                        >
                          {propertiesI18n[lang].rentalMenu}
                        </button>
                        <button
                          data-testid="mobile-nav-sub-sold"
                          onClick={() => {
                            setOpen(false);
                            navigate("/objekte/verkauft");
                          }}
                          className="text-left text-[12px] font-medium uppercase tracking-[0.25em] text-champagne"
                        >
                          {propertiesI18n[lang].soldMenu}
                        </button>
                      </div>
                    )}
                  </motion.div>
                ) : (
                  <motion.button
                    key={l.id}
                    data-testid={`mobile-${l.testid}`}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1 + i * 0.08, ease: EASE }}
                    onClick={() => go(l.id)}
                    className="text-left font-serif text-4xl text-ivory"
                  >
                    {l.label}
                  </motion.button>
                )
              )}
              <motion.button
                data-testid="mobile-nav-members"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.58, ease: EASE }}
                onClick={() => {
                  setOpen(false);
                  navigate("/espace");
                }}
                className="text-left font-serif text-2xl italic text-champagne"
              >
                {lang === "fr" ? "Espace membres" : lang === "de" ? "Member-Bereich" : "Member area"}
              </motion.button>
              <motion.button
                data-testid="mobile-header-cta"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.5, ease: EASE }}
                onClick={() => go("contact")}
                className="mt-6 flex w-fit items-center gap-3 border border-bronze px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-ivory"
              >
                {t.nav.cta}
                <ArrowRight size={14} />
              </motion.button>
            </nav>
            <div className="mt-auto flex items-center gap-3 text-[12px] tracking-[0.25em] text-ivory/50">
              {LANGS.map((l, i) => (
                <span key={l} className="flex items-center gap-3">
                  {i > 0 && <span className="text-ivory/20">|</span>}
                  <button
                    data-testid={`mobile-lang-switch-${l}`}
                    onClick={() => setLang(l)}
                    className={`uppercase ${lang === l ? "font-semibold text-champagne" : ""}`}
                  >
                    {l}
                  </button>
                </span>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
